// A type's voxel model (log.ts TypeVoxels): built on the object page from its fitted instances (the wasm kernel
// wasm/objcore/src/voxel.rs, which matches voxelize here exactly), kept in the edit log, and drawn in place of
// each instance's points on the main page, one splat per cell (asCloud with the cell size as spacing).
import type { Rigid } from '../align.ts';
import { apply } from '../align.ts';
import type { TypeVoxels } from './log.ts';
import { toB64, fromB64 } from './pidset.ts';
import { octEncode } from './instances.ts';
import { octDecode } from '../math.ts';

/** Per cell, 11 numbers: i, j, k, point count, mean r, g, b (0-255, not rounded), mean normal x, y, z (unit; NaN: none), and how many instances have points in it. */
export const CELL = 11;

/**
 * Cells of edge `size` holding at least `min` used points from at least `minInst` instances (inst: each point's), of
 * points (packed xyz) moved by M (to the grid frame), sorted by i, then j, then k. Asking for several instances keeps
 * what they agree on and drops one stray instance's noise. The JS reference of the wasm kernel (voxel.rs): same
 * arithmetic, same answer.
 */
export function voxelize(p: Float32Array, nrm: Float32Array, rgb: Uint8Array, inst: Uint16Array, use: Uint8Array, M: Rigid, size: number, min: number, minInst: number): Float64Array {
  const R = M.R, t = M.t, cells = new Map<string, number>(), acc: number[][] = [], seen = new Set<number>();
  for (let v = 0; v < use.length; v++) {
    if (!use[v]) continue;
    const x = p[3 * v], y = p[3 * v + 1], z = p[3 * v + 2];
    const i = Math.floor((R[0] * x + R[1] * y + R[2] * z + t[0]) / size);
    const j = Math.floor((R[3] * x + R[4] * y + R[5] * z + t[1]) / size);
    const k = Math.floor((R[6] * x + R[7] * y + R[8] * z + t[2]) / size);
    const key = `${i},${j},${k}`;
    let c = cells.get(key);
    if (c === undefined) { cells.set(key, (c = acc.length)); acc.push([i, j, k, 0, 0, 0, 0, 0, 0, 0, 0, 0]); }
    const a = acc[c];
    if (!seen.has(c * 65536 + inst[v])) { seen.add(c * 65536 + inst[v]); a[11]++; }
    a[3]++; a[4] += rgb[3 * v]; a[5] += rgb[3 * v + 1]; a[6] += rgb[3 * v + 2];
    const nx = nrm[3 * v];
    if (nx === nx) {   // rotate the normal into the grid frame too
      const ny = nrm[3 * v + 1], nz = nrm[3 * v + 2];
      a[7] += R[0] * nx + R[1] * ny + R[2] * nz; a[8] += R[3] * nx + R[4] * ny + R[5] * nz; a[9] += R[6] * nx + R[7] * ny + R[8] * nz; a[10]++;
    }
  }
  const keep = acc.filter(a => a[3] >= min && a[11] >= minInst).sort((a, b) => a[0] - b[0] || a[1] - b[1] || a[2] - b[2]);
  const out = new Float64Array(CELL * keep.length);
  keep.forEach((a, c) => {
    const o = CELL * c, l = Math.sqrt(a[7] * a[7] + a[8] * a[8] + a[9] * a[9]);   // not Math.hypot: it rounds differently from voxel.rs
    out[o] = a[0]; out[o + 1] = a[1]; out[o + 2] = a[2]; out[o + 3] = a[3];
    out[o + 4] = a[4] / a[3]; out[o + 5] = a[5] / a[3]; out[o + 6] = a[6] / a[3];
    if (a[10] && l > 0) { out[o + 7] = a[7] / l; out[o + 8] = a[8] / l; out[o + 9] = a[9] / l; } else out.fill(NaN, o + 7, o + 10);
    out[o + 10] = a[11];
  });
  return out;
}

/** The cells whose centres are inside a box of edge lengths s centred on the grid's origin (the type's box, when the grid is its frame). */
export function clipCells(cells: Float64Array, size: number, s: readonly number[]): Float64Array {
  const keep: number[] = [];
  for (let c = 0; c < cells.length / CELL; c++) if ([0, 1, 2].every(d => Math.abs((cells[CELL * c + d] + 0.5) * size) <= s[d] / 2)) keep.push(c);
  const out = new Float64Array(CELL * keep.length);
  keep.forEach((c, q) => out.set(cells.subarray(CELL * c, CELL * (c + 1)), CELL * q));
  return out;
}

/** The kernel's cells as a TypeVoxels (cells outside Int16 indices are dropped). */
export function encodeVoxels(cells: Float64Array, ref: string, G: Rigid, size: number): TypeVoxels {
  const m = cells.length / CELL, ok: number[] = [];
  for (let c = 0; c < m; c++) if ([0, 1, 2].every(d => Math.abs(cells[CELL * c + d]) <= 32767)) ok.push(c);
  const n = ok.length, ijk = new Int16Array(3 * n), rgb = new Uint8Array(3 * n), nrm = new Uint8Array(2 * n);
  ok.forEach((c, q) => {
    const o = CELL * c;
    for (let d = 0; d < 3; d++) { ijk[3 * q + d] = cells[o + d]; rgb[3 * q + d] = Math.round(cells[o + 4 + d]); }
    // no normal: (0, 0, 1) would shade it as facing up, so mark it with the has-normal flag off instead (decode gives NaN)
    if (cells[o + 7] === cells[o + 7]) {
      const [u, w] = octEncode(cells[o + 7], cells[o + 8], cells[o + 9]);
      nrm[2 * q] = u; nrm[2 * q + 1] = u || w ? w : 1;   // straight down encodes to (0, 0): nudge it off the no-normal mark
    }
  });
  return { ref, G, size, n, ijk: toB64(new Uint8Array(ijk.buffer)), rgb: toB64(rgb), nrm: toB64(nrm) };
}

/**
 * The cells' centres (packed xyz, in the reference instance's world: through G), mean normals and colours. A cell
 * written with no normal (octahedral 0, 0; encodeVoxels moves a normal that encodes there) decodes as NaN, as asCloud expects.
 */
export function decodeVoxels(v: TypeVoxels): { xyz: Float32Array; nrm: Float32Array; rgb: Uint8Array } {
  const n = v.n, b = fromB64(v.ijk), ijk = new Int16Array(b.buffer, b.byteOffset, 3 * n), oct = fromB64(v.nrm), R = v.G.R;
  const xyz = new Float32Array(3 * n), nrm = new Float32Array(3 * n), rgb = fromB64(v.rgb).slice(0, 3 * n);
  for (let c = 0; c < n; c++) {
    const q = apply(v.G, [(ijk[3 * c] + 0.5) * v.size, (ijk[3 * c + 1] + 0.5) * v.size, (ijk[3 * c + 2] + 0.5) * v.size]);
    xyz[3 * c] = q[0]; xyz[3 * c + 1] = q[1]; xyz[3 * c + 2] = q[2];
    const u = oct[2 * c], w = oct[2 * c + 1];
    if (!u && !w) { nrm.fill(NaN, 3 * c, 3 * c + 3); continue; }
    const [x, y, z] = octDecode(u, w);
    nrm[3 * c] = R[0] * x + R[1] * y + R[2] * z; nrm[3 * c + 1] = R[3] * x + R[4] * y + R[5] * z; nrm[3 * c + 2] = R[6] * x + R[7] * y + R[8] * z;
  }
  return { xyz, nrm, rgb };
}
